<script lang="ts">
import { reactiveLocation } from '../lib/railsUtil.svelte';
import Dropdown from './Dropdown.svelte';
import Form from './Form.svelte';

import House from "~icons/ph/house";
import HouseD from "~icons/ph/house-fill";
import Envelope from "~icons/ph/envelope-simple";
import EnvelopeD from "~icons/ph/envelope-simple-bold";
import Faq from "~icons/ph/question";
import FaqD from "~icons/ph/question-bold";
import User from "~icons/ph/user-circle";
import UserD from "~icons/ph/user-circle-bold";
import Dots from "~icons/ph/dots-three-bold";
import Door from "~icons/ph/door-open-fill";
import Team from "~icons/ph/users-three-fill";
import Shield from "~icons/ph/shield-check-bold";
import Out from "~icons/ph/arrow-square-out-bold";
import Newspaper from "~icons/ph/newspaper-fill";
import { anyForCaseType, type todoIds } from '../lib/todoIds';

let {
  links,
  unread_messages,
  unread_updates,
  todo_ids,
  case_type_id,
  show_tooltips = false
} : {
  links: { [key: string]: [title: string, path: string, linkAttrs: {[key: string]: any}] };
  unread_messages: { [key: string]: string | null };
  unread_updates: { [key: string]: number };
  todo_ids: todoIds;
  case_type_id: string;
  show_tooltips: boolean;
} = $props()

const hasUnread = (key)=>{
  switch(key){
    case "messages":
      return Object.keys(unread_messages).length > 0;
    case "case_updates":
      return Object.keys(unread_updates).length > 0;
    case "home":
      return anyForCaseType(todo_ids, case_type_id);
    default:
      return false;
  }
}
const currentLocation = reactiveLocation()
const isCurrent = (key, path)=>{
  if(key == "home") return path == currentLocation.pathname;
  return currentLocation.pathname.startsWith(path);
}
const icons = {
  "home": {IconDesktop: HouseD, IconMobile: House},
  "messages": {IconDesktop: EnvelopeD, IconMobile: Envelope},
  "faqs": {IconDesktop: FaqD, IconMobile: Faq},
  "account": {IconDesktop: UserD, IconMobile: User},
  "case_updates": {IconDesktop: Newspaper, IconMobile: Newspaper},
  "team": {IconDesktop: Team, IconMobile: Team},
  "logout": {IconDesktop: Door, IconMobile: Door},
  "privacy": {IconDesktop: Shield, IconMobile: Shield},
}

function hideTooltips(event: MouseEvent) {
  if (!show_tooltips) return;
  if (document.documentElement.dataset.tooltipsHidden) return;
  // Don't dismiss tooltips when interacting with the Welcome or Remember me modals
  const target = event?.target as Element | null;
  if (target?.closest?.("dialog[open]")) return;
  // Using a data-* attribute on the html element prevents Turbo from temporarily restoring tooltips from a cached version
  document.documentElement.dataset.tooltipsHidden = "true"
}
</script>

<svelte:window onmousedown={hideTooltips} />

<ul class="
  gap-1 grid-cols-5 grid h-full w-full
  lg:gap-0.5 lg:flex lg:flex-col
">
  {@render navLink("home")}
  {@render navLink("messages")}
  {@render navLink("faqs")}
  {@render navLink("account")}
  <Dropdown class="nav-link-box lg:hidden" position="end top" margin="mb-1 -mr-1.5">
    {#snippet button()}
      <div class="relative">
        {#if show_tooltips}{@render tooltip("Find case updates here", "right")}{/if}
        <Dots class="w-8 h-auto pt-0.5 text-base-200 mx-auto" />
      </div>
      {#if hasUnread("case_updates")}<span class="notification-dot !top-[0.125rem] !left-1/2 ml-2 !right-auto"></span>{/if}
    {/snippet}
    {@render moreLinks()}
  </Dropdown>
  <div class="hidden lg:contents">
    <div class="flex-grow"></div>
    {@render moreLinks()}
  </div>
</ul>

{#snippet navLink(key)}
  {@const [title, path, linkAttrs] = links[key]}
  {@const {class:linkClass, ...attrs} = (linkAttrs || {})}
  {@const {IconDesktop, IconMobile} = icons[key]}
  {@const disabled = path == null}
  {@const active = !disabled && isCurrent(key, path.replace(/\?.*$/, ""))}
  <li class="nav-link-box relative" class:active={active}>
    {#if show_tooltips && key === "home"}{@render tooltip("Go here for tasks", "left")}{/if}
    <a href={path}
      class:lg:bg-base-200={active}
      class:lg:border-blue={active}
      class:pointer-events-none={disabled}
      class:opacity-25={disabled}
      class="
        text-white hover:text-white hover:bg-transparent {linkClass||''}
        max-lg:items-center max-lg:flex max-lg:flex-col max-lg:justify-center max-lg:p-0 max-lg:h-full
        border-transparent lg-nav-link
      " {title} {...attrs}>
      <span class="icon-container">
        <IconMobile class="w-7 h-7 lg:hidden"/>
        <IconDesktop class="w-6 h-6 hidden lg:block" />
        {#if hasUnread(key)}<span class="notification-dot"></span>{/if}
      </span>
      <span class="hidden lg:contents">{title}</span>
    </a>
  </li>
{/snippet}

{#snippet moreLink(key)}
  {#if links[key]}
    {@const [title, path, linkAttrs] = links[key]}
    {@const {class:linkClass, ...attrs} = (linkAttrs || {})}
    {@const {IconDesktop, IconMobile} = icons[key]}
    {@const active = isCurrent(key, path.replace(/\?.*$/, ""))}
    <li class="relative">
      {#if show_tooltips && key === "case_updates"}{@render tooltip("Find case updates here", "desktop-only")}{/if}
      <a href={path}
        class:lg:bg-base-200={active}
        class:lg:border-blue={active}
        class="
          no-underline text-blue font-normal lg:hover:bg-base-200 {linkClass||''}
          border-transparent lg-nav-link
        " {title} {...attrs}>
        <span class="icon-container -ml-1 mr-1">
          <IconMobile class="w-7 h-7 lg:hidden" />
          <IconDesktop class="w-6 h-6 hidden lg:block" />
          {#if hasUnread(key)}<span class="notification-dot"></span>{/if}
        </span>
        {title}
        {#if attrs.target == "_blank"}<Out class="w-4 h-4 inline-block -mt-0.5" />{/if}
      </a>
    </li>
  {/if}
{/snippet}

{#snippet moreLinks()}
  {@render moreLink("team")}
  {@render moreLink("case_updates")}
  {@render moreLink("privacy")}
  <li class="mx-1.5 my-1"></li>
  <li>
    <button data-viewer-mode class="text-blue font-normal border-transparent lg-nav-link" form="logout-form">
      <span class="icon-container -ml-1 mr-1"><Door class="w-7 h-7 lg:w-6 lg:h-6" /></span>
      {links.logout[0]}
    </button>
    <Form action={links.logout[1]} method="delete" class="hidden" id="logout-form" data-turbo="false" />
  </li>
{/snippet}

{#snippet tooltip(text, variant)}
  <div class={["nav-tooltip absolute -top-8 lg:top-1/2 -translate-y-1/2",
    {"left-0 lg:left-28 before:left-[min(6.5vw,5rem)]": variant === "left"},
    {"right-0 before:right-[min(7vw,5rem)]": variant === "right"},
    {"hidden lg:block lg:left-32": variant === "desktop-only"}
  ]}>
    <span class="relative">{text}</span>
  </div>
{/snippet}

<style lang="postcss">
.notification-dot {
  @apply absolute bg-warning rounded-full w-2.5 h-2.5 -top-0.5 -right-1;
}
.nav-link-box {
  @apply max-lg:relative max-lg:rounded-full max-lg:block;
  &.active { @apply max-lg:bg-white/20; }
}
.lg-nav-link {
  @apply lg:py-3 lg:rounded-none;
  @apply lg:font-medium lg:text-base lg:text-blue lg:hover:text-blue lg:hover:bg-base-200;
  @apply lg:border-0 lg:border-solid lg:border-l-4;
}
/* Match parent nav lg:rounded (overflow-clip would crop the tooltips, which extend past the sidebar on desktop) */
.nav-link-box:first-child .lg-nav-link {
  @apply lg:rounded-t;
}
div:last-child > li:last-child .lg-nav-link {
  @apply lg:rounded-b;
}
.icon-container {
  @apply relative block lg:-ml-1 lg:mr-1;
}
.nav-tooltip {
  @apply text-white bg-black py-1 px-2 text-sm font-medium rounded pointer-events-none w-max transition-opacity duration-200 ease-out;
  @apply before:absolute before:content-[''] before:rotate-45 before:bg-black before:rounded before:z-0 before:h-6 before:w-6 lg:before:h-4 lg:before:w-4;
  @apply before:-bottom-1 lg:before:-translate-y-1/2 lg:before:top-1/2 lg:before:-translate-x-1/2 lg:before:left-1 lg:before:right-auto;
}
:global(html[data-tooltips-hidden] .nav-tooltip) {
  @apply opacity-0;
}
</style>
